import { AlertCircle } from "lucide-react";
import { useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useRecordingsBySessionId } from "@/features/recordings/api/fetch-recordings";
import { NoRecordings } from "@/features/recordings/components/no-recordings";
import { RecordingItem } from "@/features/recordings/components/recording-item";
import { RecordingItemSkeleton } from "@/features/recordings/components/recording-item-skeleton";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useGetSession } from "@/features/session/api/get-session";

export function RecordingsBySessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();

  const {
    error,

    isLoading,
    isError,
    isSuccess,
    data: recordings,
  } = useRecordingsBySessionId({ sessionId: sessionId ?? "" });
  const {
    data: session,
    isLoading: isSessionLoading,
    isError: isSessionError,
    isSuccess: isSessionSuccess,
    error: sessionError,
  } = useGetSession(sessionId ?? "");

  if (isLoading || isSessionLoading)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View session recordings"
          className="mb-6"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
        </div>
      </section>
    );

  if (isError || !isSuccess || isSessionError || !isSessionSuccess)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View session recordings"
          className="mb-6"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ??
              sessionError?.message ??
              "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      </section>
    );

  return (
    <section>
      <OnboardingHeader
        title={session.name + " - Recordings"}
        description="View session recordings"
        className="mb-6"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {recordings.map((recording) => (
          <RecordingItem key={recording.id} recording={recording} />
        ))}
      </div>

      {recordings.length === 0 && <NoRecordings />}
    </section>
  );
}
